import React, { useState, useEffect } from "react";
import { Box, Button, TextField, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import { Formik } from "formik";
import * as yup from "yup";
import { useMediaQuery } from "@mui/material";
import { tokens } from "../../theme";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "../../api/axios";
import PasswordField from "../../components/PasswordField";
import useAuth from "../../hooks/useAuth";
import ForgetPasswordOTPModal from "../../components/modals/ForgetPasswordOTPModal";
import OTPModal from "../../components/modals/OTPModal";

const LOGIN_URL = "/auth-serviceV3/auth/staff/login";
const VERIFY_OTP_URL = "/auth-serviceV3/auth/login/verify-otp";
const DEVICE_VERIFY_URL = "/auth-serviceV3/api/v1/device-change/verify";
const DEVICE_RESEND_URL = "/auth-serviceV3/api/v1/device-change/resend";

const Login = () => {
  const { login, success, snackError, info } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [modal, setModal] = useState({ open: false });
  const [otpModal, setOTPModal] = useState({
    open: false,
    emailOrPhone: "",
    word: "",
  });

  // Telemetry, 2FA & Device Change States
  const [ipAddress, setIpAddress] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [loginIdState, setLoginIdState] = useState("");
  const [showDeviceVerification, setShowDeviceVerification] = useState(false);
  const [verificationToken, setVerificationToken] = useState("");
  const [deviceOtp, setDeviceOtp] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [isVerifyingDevice, setIsVerifyingDevice] = useState(false);
  const [isResendingDeviceOtp, setIsResendingDeviceOtp] = useState(false);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const isNonMobile = useMediaQuery("(min-width:600px)");

  const getIpAddress = async () => {
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      return data.ip;
    } catch (error) {
      return "N/A";
    }
  };

  useEffect(() => {
    setDeviceId(navigator.userAgent);
    const fetchIp = async () => {
      const ip = await getIpAddress();
      setIpAddress(ip);
    };
    fetchIp();
  }, []);

  const handleSuccessfulLogin = (res) => {
    const data = res?.data;
    const userObj = data?.user || data;
    const userType = userObj?.userType || data?.userType || res?.userType;

    if (userType === 'ADMIN') {
      const accessToken = data?.accessToken || userObj?.accessToken || res?.accessToken;
      const refreshToken = data?.refreshToken || userObj?.refreshToken || res?.refreshToken;

      localStorage.setItem("userDetails", JSON.stringify({ token: accessToken, user: userObj }));
      localStorage.setItem("token", accessToken);
      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
      }

      if (userObj?.userId) localStorage.setItem("userId", userObj?.userId);
      if (userObj?.phoneNumber) localStorage.setItem("phoneNumber", userObj?.phoneNumber);
      if (userObj?.email) localStorage.setItem("email", userObj?.email);

      const rawRoles = userObj?.roles || data?.roles || res?.roles || [];
      const roles = Array.isArray(rawRoles) ? rawRoles : [rawRoles];

      // Normalize role names to ROLE_ADMIN_SUPER / ROLE_ADMIN_OWNER for compatibility with support panel routing restrictions
      const mappedRoles = roles.map(role => {
        if (role === "SUPER_ADMIN" || role === "ROLE_SUPER_ADMIN") return "ROLE_ADMIN_SUPER";
        if (role === "ADMIN" || role === "ROLE_ADMIN" || role === "OWNER" || role === "ROLE_OWNER") return "ROLE_ADMIN_OWNER";
        return role;
      });
      const finalizedRoles = [...new Set([...roles, ...mappedRoles])];

      localStorage.setItem("roles", JSON.stringify(finalizedRoles));

      const firstName = userObj?.firstName || data?.firstName || res?.firstName;
      const lastName = userObj?.lastName || data?.lastName || res?.lastName;
      const surname = userObj?.surname || data?.surname || res?.surname;

      if (firstName && (lastName || surname)) {
        localStorage.setItem("fullName", `${firstName} ${lastName || surname}`);
      }

      const normalizedUser = {
        ...userObj,
        roles: finalizedRoles
      };
      login(JSON.stringify({ user: normalizedUser, token: accessToken }));
      success(res.message || "Welcome back!");

      const isMarketerOnly = finalizedRoles.includes('MARKETER') && finalizedRoles.length === 1;
      navigate(isMarketerOnly ? "/campaigns" : from, { replace: true });
    } else {
      snackError("Unauthorized: Support Panel access is restricted to Admin users.");
    }
  };

  const handleOTPSubmit = async (otp, setIsSubmitting) => {
    setIsSubmitting(true);
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      snackError("Please enter a valid 6-digit OTP.");
      setIsSubmitting(false);
      return;
    }

    const payload = {
      identifier: loginIdState,
      otp: otpString,
      purpose: "LOGIN",
      deviceInfo: {
        deviceId: deviceId || navigator.userAgent,
        deviceName: navigator.platform || "WEB",
        deviceType: "WEB",
        os: navigator.oscpu || navigator.platform || "Unknown OS",
        version: navigator.appVersion || "1.0",
        userAgent: navigator.userAgent,
        ipAddress: ipAddress || "N/A",
      },
    };

    try {
      const response = await axios.post(
        VERIFY_OTP_URL,
        payload
      );
      const res = response.data;
      if (res.success) {
        handleSuccessfulLogin(res);
        setOTPModal({ open: false, emailOrPhone: "", word: "" });
      } else {
        snackError(res.message || "Invalid OTP. Please try again.");
      }
    } catch (error) {
      const errMsg = error.response?.data?.message || "OTP verification failed.";
      snackError(errMsg);
    }
    setIsSubmitting(false);
  };

  const handleFormSubmit = async (values, actions) => {
    const { emailOrPhone, password } = values;
    setLoginIdState(emailOrPhone);

    const payload = {
      loginId: emailOrPhone,
      password: password,
      deviceInfo: {
        deviceId: deviceId || navigator.userAgent,
        deviceName: navigator.platform || "WEB",
        deviceType: "WEB",
        os: navigator.oscpu || navigator.platform || "Unknown OS",
        version: navigator.appVersion || "1.0",
        userAgent: navigator.userAgent,
        ipAddress: ipAddress || "N/A",
      },
    };

    try {
      const response = await axios.post(
        LOGIN_URL,
        payload
      );
      const res = response.data;

      if (res.success) {
        if (res.data?.requiresDeviceVerification) {
          setVerificationToken(res.data.verificationToken);
          setShowDeviceVerification(true);
          info('New Device Detected: ' + (res.message || "Please verify your device."));
        } else if (res.data?.requiresOtp) {
          setOTPModal((prev) => ({
            ...prev,
            open: true,
            emailOrPhone: emailOrPhone,
            word: password,
          }));
          info('2FA Required: ' + (res.message || "Please enter your OTP."));
        } else {
          handleSuccessfulLogin(res);
        }
        actions.resetForm();
      } else {
        snackError("Login Failed: " + (res.message || "Invalid credentials"));
      }
    } catch (error) {
      const errMsg = error.response?.data?.message || error.message || "Error: Something went wrong. Please try again.";
      snackError(errMsg);
    }
  };

  const handleDeviceVerificationSubmit = async (e) => {
    e.preventDefault();
    if (!deviceOtp || deviceOtp.length !== 6) {
      snackError("Please enter a valid 6-digit OTP.");
      return;
    }
    if (!dateOfBirth) {
      snackError("Please select/enter your Date of Birth.");
      return;
    }

    setIsVerifyingDevice(true);
    const payload = {
      verificationToken,
      otp: deviceOtp,
      dateOfBirth: dateOfBirth,
    };

    try {
      const response = await axios.post(
        DEVICE_VERIFY_URL,
        payload
      );
      const res = response.data;
      if (res.success) {
        setShowDeviceVerification(false);
        setDeviceOtp("");
        setDateOfBirth("");
        handleSuccessfulLogin(res);
      } else {
        snackError(res.message || "Device verification failed.");
      }
    } catch (error) {
      const errMsg = error.response?.data?.message || "Device verification failed.";
      snackError(errMsg);
    }
    setIsVerifyingDevice(false);
  };

  const handleResendDeviceOtp = async () => {
    if (!verificationToken) {
      snackError("No active verification token found.");
      return;
    }
    setIsResendingDeviceOtp(true);
    try {
      const response = await axios.post(
        `${DEVICE_RESEND_URL}?verificationToken=${encodeURIComponent(verificationToken)}`,
        {}
      );
      const res = response.data;
      if (res.success) {
        success(res.message || "Verification code resent successfully.");
      } else {
        snackError(res.message || "Resend failed.");
      }
    } catch (error) {
      const errMsg = error.response?.data?.message || "Resend verification code failed.";
      snackError(errMsg);
    }
    setIsResendingDeviceOtp(false);
  };

  const initialValues = {
    emailOrPhone: "",
    password: "",
  };

  const checkoutSchema = yup.object().shape({
    emailOrPhone: yup
      .string("Enter your Email/Phone Number")
      .required("Email/Phone Number is required")
      .test("emailOrPhone", "Enter Valid Phone/Email", function (value) {
        const emailRegex =
          /^([a-zA-Z0-9_\.\-])+\@(([a-zA-Z0-9\-])+\.)+([a-zA-Z0-9]{2,4})+$/;

        const phoneRegex = /^(\+234-|\+234|0)?\d{10}$/; // Change this regex based on requirement
        let isValidEmail = emailRegex.test(value);
        let isValidPhone = phoneRegex.test(value);
        if (!isValidEmail && !isValidPhone) {
          return false;
        }
        return true;
      }),
    password: yup.string().min(3).required("Required"),
  });

  return (
    <Box
      m="20px"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      height="90vh"
      alignItems="center"
      textAlign="center"
    >
      <OTPModal
        setModal={setOTPModal}
        modal={otpModal}
        handleOTPSubmit={handleOTPSubmit}
      />
      <ForgetPasswordOTPModal modal={modal} setModal={setModal} />

      {/* New Device Verification Dialog */}
      <Dialog
        open={showDeviceVerification}
        onClose={() => setShowDeviceVerification(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.primary[400],
            backgroundImage: "none",
            borderRadius: "16px",
            p: 3,
          }
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          <Typography
            variant="h3"
            color={colors.grey[100]}
            fontWeight="bold"
            sx={{ mb: "5px" }}
          >
            Verify New Device
          </Typography>
          <Typography variant="h6" color={colors.orangeAccent[600]}>
            A new device was detected. Please verify below.
          </Typography>
        </DialogTitle>
        <form onSubmit={handleDeviceVerificationSubmit}>
          <DialogContent sx={{ py: 2 }}>
            <Typography variant="body2" color={colors.grey[200]} sx={{ mb: 3, textAlign: "center" }}>
              Please enter the OTP sent to your device and your Date of Birth to authorize this login.
            </Typography>
            <Box display="flex" flexDirection="column" gap={3}>
              <TextField
                fullWidth
                variant="filled"
                label="OTP Verification Code"
                value={deviceOtp}
                onChange={(e) => setDeviceOtp(e.target.value)}
                placeholder="••••••"
                inputProps={{ maxLength: 6 }}
                required
              />
              <TextField
                fullWidth
                variant="filled"
                label="Date of Birth"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                required
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ flexDirection: "column", gap: 2, px: 3, pb: 2 }}>
            <Button
              fullWidth
              type="submit"
              color="secondary"
              variant="contained"
              disabled={isVerifyingDevice}
              sx={{ py: 1.5, borderRadius: "8px", fontWeight: "bold" }}
            >
              {isVerifyingDevice ? <CircularProgress size={24} color="inherit" /> : "Verify Device"}
            </Button>
            <Box display="flex" justifyContent="space-between" width="100%">
              <Button
                size="small"
                onClick={handleResendDeviceOtp}
                disabled={isResendingDeviceOtp}
                sx={{ color: colors.orangeAccent[600], fontWeight: 500 }}
              >
                {isResendingDeviceOtp ? "Resending..." : "Resend OTP"}
              </Button>
              <Button
                size="small"
                onClick={() => setShowDeviceVerification(false)}
                sx={{ color: colors.grey[300] }}
              >
                Cancel
              </Button>
            </Box>
          </DialogActions>
        </form>
      </Dialog>

      <Box mb="30px">
        <Typography
          variant="h2"
          color={colors.grey[100]}
          fontWeight="bold"
          sx={{ mb: "5px" }}
        >
          LOGIN
        </Typography>
        <Typography variant="h5" color={colors.orangeAccent[600]}>
          Login to see Dashboard
        </Typography>
      </Box>

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={checkoutSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(2, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 10" },
              }}
            >
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Email / Phone"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.emailOrPhone}
                name="emailOrPhone"
                error={!!touched.emailOrPhone && !!errors.emailOrPhone}
                helperText={touched.emailOrPhone && errors.emailOrPhone}
                sx={{ gridColumn: "span 16" }}
              />
              <PasswordField
                fullWidth
                variant="filled"
                type={showPassword ? "text" : "password"}
                label="Password"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.password}
                name="password"
                error={!!touched.password && !!errors.password}
                helperText={touched.password && errors.password}
                sx={{ gridColumn: "span 16" }}
                iconEnd={<Visibility />}
                setShowPassword={setShowPassword}
              />
            </Box>
            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                fullWidth
                disabled={isSubmitting}
                type="submit"
                color="secondary"
                variant="contained"
                sx={{ gridColumn: "span 16", p: 2, mt: 1 }}
              >
                Login
              </Button>
            </Box>
            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                onClick={() => setModal({ open: true })}
                sx={{ color: colors.orangeAccent[600], fontSize: "16px" }}
              >
                forget password?
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default Login;
